import { TeIcon, TeButtonOutline } from '@topcon/tuicl-react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { BusinessPartnerBody } from '../../api/generated/pm';
import { BusinessPartnerType } from '../../app/routes/utils';
import { background, border, text } from '../../providers/theme/theme.index';
import { formatAddress } from '../../utils/addressFormat';

export { formatAddress };

interface Props {
  organization: BusinessPartnerBody;
  openServiceAgreement: (org: BusinessPartnerBody) => void;
  level?: number;
  isLastChild?: boolean;
}

const PartnerContainer = styled.div<{ $level: number }>`
  display: flex;
  width: 100%;
  padding-left: ${(props) => props.$level * 48}px;
  position: relative;
  box-sizing: border-box;
`;

const SubOrgLines = styled.div<{ $level: number; $isLastChild: boolean }>`
  position: absolute;
  left: ${(props) => (props.$level - 1) * 48 + 24}px;
  top: -8px;
  width: 24px;
  height: calc(100% + 8px);
  display: ${(props) => (props.$level > 0 ? 'block' : 'none')};
  pointer-events: none;

  .horizontal {
    position: absolute;
    top: 44px;
    left: 0;
    width: 24px;
    height: 2px;
    background-color: #b8b8b8;
  }

  .vertical {
    position: absolute;
    top: 0;
    left: 0;
    width: 2px;
    height: ${(props) => (props.$isLastChild ? '44px' : 'calc(100% + 8px)')};
    background-color: #b8b8b8;
  }
`;

const OrgCard = styled.div<{ $isChild: boolean }>`
  display: flex;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
  padding: 20px;
  background: ${background.primary};
  border: 1px solid ${border.secondary};
  border-radius: 8px;
  gap: 16px;
  margin-bottom: 8px;
`;

const OrgIcon = styled.div<{ $isChild: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${background.primary};
  border: 1px solid ${border.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const BpInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 6px;
  min-width: 0;
`;

const BpNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`;

const BpName = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  text-align: left;
`;

const BpExtId = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 400;
  color: ${text.primary};
  background-color: ${background.primary};
  border: 1px solid ${border.secondary};
  padding: 4px 12px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
`;

const BpTypeBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background-color: ${background.tertiary};
  border: 1px solid ${border.secondary};
  padding: 2px 6px;
  border-radius: 16px;
  width: fit-content;
`;

const BpTypeIcon = styled.div`
  width: 16px;
  height: 16px;
  color: ${text.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BpTypeText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${text.secondary};
`;

const BpAddress = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: ${text.secondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ButtonWrapper = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  align-self: center;
`;

export const formatURN = (org: BusinessPartnerBody) => {
  const parts = org.extId.split(':');
  if (parts.length < 5) return org.extId;
  const customerNumber = parts[4];
  const dashed = customerNumber.match(/.{1,3}/g)?.join('-') ?? customerNumber;
  return dashed;
};

function Organization({
  organization,
  openServiceAgreement,
  level = 0,
  isLastChild = false,
}: Props) {
  const { t } = useTranslation('organizations');

  const handleClick = () => {
    openServiceAgreement(organization);
  };

  const getOrganizationType = () => {
    if (organization.type === BusinessPartnerType.Topcon) {
      return t('topconOrganizationLabel');
    }
    if (organization.type === BusinessPartnerType.Dealer) {
      return t('dealerOrganizationLabel');
    }
    // For end_customer_enterprise and end_customer_individual
    return t('customerOrganizationLabel');
  };

  return (
    <PartnerContainer $level={level}>
      {level > 0 && (
        <SubOrgLines $level={level} $isLastChild={isLastChild}>
          <div className="horizontal" />
          <div className="vertical" />
        </SubOrgLines>
      )}
      <OrgCard $isChild={level > 0}>
        <OrgIcon $isChild={level > 0}>
          <TeIcon size="l" name="corporate_fare" color="icon.primary" />
        </OrgIcon>
        <BpInfo>
          <BpNameRow>
            <BpName>{organization.name}</BpName>
            <BpExtId>{formatURN(organization)}</BpExtId>
          </BpNameRow>
          <BpTypeBadge>
            <BpTypeIcon>
              <TeIcon size="s" name="domain" color="icon.secondary" />
            </BpTypeIcon>
            <BpTypeText>{getOrganizationType()}</BpTypeText>
          </BpTypeBadge>
          <BpAddress>{formatAddress(organization.companyAddress)}</BpAddress>
        </BpInfo>
        <ButtonWrapper>
          <TeButtonOutline text={t('startSupportSession')} size="m" teclick={handleClick} />
        </ButtonWrapper>
      </OrgCard>
    </PartnerContainer>
  );
}

export default Organization;
